import { useEffect } from "react";
import FormFields from "../../components/common/FormFields";
import {
  createColumn,
  getRegister,
  formatDateTime,
  addSelectItem,
  numberFormat,
} from "../../utils/helpers";
import { URL_PAYMENT_REQUESTS } from "../../utils/urls";
import useGet from "../common/useGet";
import { API_PAYMENT_MODES, API_INVOICES } from "../../utils/apis";
import { TFormHookParams } from "../../types/common";

// Helper: Format Status
const formatStatus = (status) => {
  const statusMap = {
    A: "Unpaid",
    P: "Pending",
    R: "Rejected",
    C: "Paid",
    F: "Failed",
  };
  return statusMap[status] || status;
};

export const usePaymentRequests = () => {
  // The register layout already fetches API_PAYMENT_REQUESTS_PAGINATED with
  // the active filters, so this hook must not fetch it again.

  const tableColumns = () => [
    createColumn("invoice_number", true, "Invoice Number"),
    createColumn("transaction_id", true, "Transaction ID"),
    createColumn("amount", false),
    createColumn("charge", false),
    createColumn("payment_mode_name", true),
    createColumn("phone_number", true),
    createColumn("status", true),
    createColumn("created_at", {
      type: "date",
      dateRangeNames: {
        startDateKey: "payment_date_min_date",
        endDateKey: "payment_date_max_date",
      },
    }),
    // createColumn("actions", false),
  ];

  const tableData = (data) => {
    if (!data) return [];

    return data.map((item) => ({
      ...item,
      invoice_number: item.invoice_details?.invoice_number || "N/A",
      payment_mode_name: item.payment_mode_name,
      amount: numberFormat(item?.amount ? item.amount : 0),
      status: formatStatus(item.status),
      created_at: formatDateTime(item.created_at),
      updated_at: formatDateTime(item.updated_at),
    }));
  };

  const viewData = (item) => {
    if (!item) {
      return {
        sections: [
          {
            title: "Error",
            fields: { message: "No data available" },
          },
        ],
      };
    }

    return {
      sections: [
        {
          title: "Payment Request Information",
          fields: {
            ref: item.ref,
            transaction_id: item.transaction_id,
            amount: item.amount ? `${item.amount}` : "N/A",
            status: formatStatus(item.status),
            phone_number: item.phone_number,
            payment_mode_name: item.payment_mode_name,
            declaration: item.declaration ? "Accepted" : "Not accepted",
            deleted: item.deleted ? "Yes" : "No",
          },
        },
        {
          title: "Invoice Details",
          fields: {
            invoice_number: item.invoice_details?.invoice_number || "N/A",
            invoice_amount: item.invoice_details?.amount || "N/A",
            invoice_type: item.invoice_details?.type || "N/A",
            exempted: item.invoice_details?.exempted ? "Yes" : "No",
            object_id: item.invoice_details?.object_id || "N/A",
          },
        },
        {
          title: "System Information",
          fields: {
            record_id: item.id,
            created_at: formatDateTime(item.created_at),
            updated_at: formatDateTime(item.updated_at),
            created_by: item.created_by
              ? `${item.created_by.first_name} ${item.created_by.last_name}`
              : "System",
            updated_by: item.updated_by
              ? `${item.updated_by.first_name} ${item.updated_by.last_name}`
              : "Not updated",
            invoice_id: item.invoice || "N/A",
            payment_mode_id: item.payment_mode || "N/A",
          },
        },
      ],
    };
  };

  // const formRoute = getForm(URL_PAYMENT_REQUESTS);
  const activityFilter = "payment_requests";

  return {
    tableData,
    tableColumns,
    viewData,
    activityFilter,
    // formRoute,
  };
};

export const usePaymentRequestForm = ({}: TFormHookParams = {}) => {
  const {
    loadData: loadPaymentModes,
    loading: loadingPaymentModes,
    data: paymentModes,
  } = useGet();

  const {
    loadData: loadInvoices,
    loading: loadingInvoices,
    data: invoices,
  } = useGet();

  useEffect(() => {
    loadPaymentModes({ api: API_PAYMENT_MODES });
    loadInvoices({ api: API_INVOICES });
  }, []);

  const fields = [
    {
      field_name: "invoice",
      label: "Invoice",
      type: "select",
      props: { required: true },
      options_data: invoices?.results?.map((invoice) =>
        addSelectItem({
          label: `${invoice.invoice_number} - ${invoice.amount}`,
          value: invoice.id,
        }),
      ),
      loading: loadingInvoices,
      visible: true,
    },

    {
      field_name: "payment_mode",
      label: "Payment Mode",
      type: "select",
      props: { required: true },
      options_data: paymentModes?.results,
      loading: loadingPaymentModes,
      visible: true,
    },

    {
      field_name: "phone_number",
      label: "Phone Number",
      type: "tel",
      props: {
        required: true,
        placeholder: "Enter phone number (e.g., 256758345060)",
      },
      visible: true,
    },

    {
      field_name: "amount",
      label: "Amount",
      type: "number",
      props: {
        required: true,
        min: 0,
        step: 0.01,
        placeholder: "Enter payment amount",
      },
      visible: true,
    },

    {
      field_name: "ref",
      label: "Reference",
      type: "text",
      props: {
        required: false,
        placeholder: "Payment reference (auto-generated if empty)",
      },
      visible: true,
    },

    {
      field_name: "transaction_id",
      label: "Transaction ID",
      type: "text",
      props: {
        required: false,
        placeholder: "External transaction ID",
      },
      visible: true,
    },
  ];

  const sections = [
    {
      title: "Payment Details",
      fields: fields.slice(0, 4), // Invoice, payment mode, phone, amount
    },
    {
      title: "Transaction Information",
      fields: fields.slice(4), // Reference and transaction ID
    },
  ];

  const redirect = getRegister(URL_PAYMENT_REQUESTS);

  const defaultValues = {
    amount: 500.0,
    phone_number: "256758345060",
  };

  return {
    fields,
    sections,
    redirect,
    defaultValues,
  };
};

export const PaymentRequestForm = ({
  form,
  error,
  updateFormValue,
  onChange,
}) => {
  const { fields } = usePaymentRequestForm({
    form,
    updateFormValues: updateFormValue,
  });

  // Filter visible fields
  const visibleFields = fields.filter((field) => field.visible !== false);

  return (
    <FormFields
      fields={visibleFields}
      error={error}
      form={form}
      updateFormValue={updateFormValue}
      onChange={onChange}
    />
  );
};
